/**
 * 내 기계 예측 · 재조정(SR-NET-03 · ADR-F06) — 다른 기계는 보간만, 내 기계는 로컬 커널로 입력을 곧바로 반영한다.
 *  - 이름표(label): 로컬 스텝의 틱 = 서버 틱 추정 + 앞섬(RTT/2 + 여유) — 내가 보낸 입력이 서버에서 반영될 틱
 *  - 서버 스냅샷(틱 T)의 내 기계 자세와 로컬 기록(이름표 T)의 차체 오차가 5 cm 를 넘으면: 로컬을 서버 자세(T)로 되돌리고
 *    T 이후 입력으로 지금 이름표까지 다시 돌린다(되감기) · 그림은 0.2초에 걸쳐 새 자세로 옮긴다(오프셋 감쇠)
 *  - 5 cm 이하는 두고 본다(떨림에 흔들리지 않게)
 * 로컬 월드는 PredictWorld 로 주입한다(실제: 커널 놀이터 · 시험: 가짜).
 */
import type { RemoteInput } from "@picnic-field/local-host/protocol";
import type { PredictWorld } from "../types";

export const RECONCILE_THRESHOLD_M = 0.05;
export const SMOOTH_MS = 200;
const HISTORY = 180;
const MAX_RESIM = 60;
const IDLE: RemoteInput = { dpad: null, a: false, b: false, slider: 0 };

export class Predictor {
  /** 로컬 월드가 지금 서 있는 이름표 */
  label: number | null = null;
  corrections = 0;
  private readonly history = new Map<number, Float32Array>();
  /** [이름표, 입력] — 그 이름표 스텝부터 적용(오름차순) */
  private inputs: [number, RemoteInput][] = [];
  private readonly cur: Float32Array;
  private offset: { d: [number, number, number]; at: number } | null = null;

  constructor(
    readonly world: PredictWorld,
    private readonly now: () => number,
  ) {
    this.cur = new Float32Array(world.bodyCount * 7);
  }

  /** 서버 자세(틱 T)에서 시작 */
  sync(tick: number, bodies: Float32Array, vel: Float32Array | null = null): void {
    this.world.set(bodies, vel);
    this.label = tick;
    this.history.clear();
    this.world.read(this.cur);
    this.history.set(tick, this.cur.slice());
  }

  /** 입력 — 다음 로컬 스텝부터(그 이름표를 돌려준다 · 서버로 보내는 입력의 tick) */
  input(r: RemoteInput): number {
    const at = (this.label ?? 0) + 1;
    this.inputs = this.inputs.filter(([l]) => l < at);
    this.inputs.push([at, r]);
    if (this.inputs.length > HISTORY) this.inputs.splice(0, this.inputs.length - HISTORY);
    return at;
  }

  private inputAt(label: number): RemoteInput {
    let r: RemoteInput = IDLE;
    for (const [l, x] of this.inputs) if (l <= label) r = x;
    return r;
  }

  /** 이름표 target 까지 로컬로 돈다(한 번에 ≤ 8스텝 — 밀리면 따라잡지 않는다) */
  advanceTo(target: number): void {
    this.stepTo(target, 8);
  }

  private stepTo(target: number, maxSteps: number): void {
    if (this.label === null) return;
    if (target - this.label > maxSteps) this.label = target - maxSteps;
    while (this.label < target) {
      this.label++;
      this.world.step(this.inputAt(this.label));
      this.world.read(this.cur);
      this.history.set(this.label, this.cur.slice());
    }
    for (const k of this.history.keys()) if (k < this.label - HISTORY) this.history.delete(k);
  }

  /**
   * 서버 자세(틱 T · 내 기계 몸체) — 로컬 기록 T 와 차체 오차가 문턱을 넘으면 되감아 다시 돈다. 되감았으면 true.
   * vel: 서버 스냅샷 두 개로 어림한 속도(없으면 0 에서)
   */
  reconcile(tick: number, bodies: Float32Array, vel: Float32Array | null = null): boolean {
    if (this.label === null) {
      this.sync(tick, bodies, vel);
      return false;
    }
    const mine = this.history.get(tick);
    if (!mine) {
      // 기록이 없다(너무 옛날 · 이름표 앞) — 로컬이 서버보다 뒤처졌으면 서버에서 다시 시작
      if (tick > this.label) {
        this.sync(tick, bodies, vel);
        return true;
      }
      return false;
    }
    const err = Math.hypot(mine[0]! - bodies[0]!, mine[1]! - bodies[1]!, mine[2]! - bodies[2]!);
    if (err <= RECONCILE_THRESHOLD_M) return false;
    // 되감기 — 그리던 자리에서 새 자리로 부드럽게(0.2초)
    const before = this.render(new Float32Array(this.cur.length));
    const target = this.label;
    this.world.set(bodies, vel);
    this.label = tick;
    this.history.clear();
    this.world.read(this.cur);
    this.history.set(tick, this.cur.slice());
    this.stepTo(target, MAX_RESIM);
    this.offset = {
      d: [before[0]! - this.cur[0]!, before[1]! - this.cur[1]!, before[2]! - this.cur[2]!],
      at: this.now(),
    };
    this.corrections++;
    return true;
  }

  /** 그릴 내 기계 자세(m) — 재조정 직후 0.2초는 옛 자리에서 옮겨 간다 */
  render(out: Float32Array): Float32Array {
    out.set(this.cur);
    if (this.offset) {
      const k = 1 - (this.now() - this.offset.at) / SMOOTH_MS;
      if (k <= 0) this.offset = null;
      else
        for (let o = 0; o + 6 < out.length; o += 7) {
          out[o] = out[o]! + this.offset.d[0] * k;
          out[o + 1] = out[o + 1]! + this.offset.d[1] * k;
          out[o + 2] = out[o + 2]! + this.offset.d[2] * k;
        }
    }
    return out;
  }

  dispose(): void {
    this.world.dispose();
  }
}

/** 두 서버 자세(Δt 초)로 몸체별 속도(선 m/s · 각 rad/s) 어림 */
export function velocityOf(a: Float32Array, b: Float32Array, dt: number): Float32Array {
  const n = Math.min(a.length, b.length) / 7;
  const v = new Float32Array(n * 6);
  if (dt <= 0) return v;
  for (let i = 0; i < n; i++) {
    const o = i * 7;
    v[i * 6] = (b[o]! - a[o]!) / dt;
    v[i * 6 + 1] = (b[o + 1]! - a[o + 1]!) / dt;
    v[i * 6 + 2] = (b[o + 2]! - a[o + 2]!) / dt;
    // q_rel = q_b · conj(q_a) → 축·각
    const [ax, ay, az, aw] = [-a[o + 3]!, -a[o + 4]!, -a[o + 5]!, a[o + 6]!];
    const [bx, by, bz, bw] = [b[o + 3]!, b[o + 4]!, b[o + 5]!, b[o + 6]!];
    let x = bw * ax + bx * aw + by * az - bz * ay;
    let y = bw * ay - bx * az + by * aw + bz * ax;
    let z = bw * az + bx * ay - by * ax + bz * aw;
    let w = bw * aw - bx * ax - by * ay - bz * az;
    if (w < 0) [x, y, z, w] = [-x, -y, -z, -w];
    const s = Math.hypot(x, y, z);
    const ang = 2 * Math.atan2(s, w);
    const k = s > 1e-9 ? ang / s / dt : 0;
    v[i * 6 + 3] = x * k;
    v[i * 6 + 4] = y * k;
    v[i * 6 + 5] = z * k;
  }
  return v;
}
